import React, { useState, useEffect, useRef } from "react";
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Stack,
  Divider,
  Paper,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PersonIcon from "@mui/icons-material/Person";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import { useGetYoutubeRequests } from "../../../services/stagemode_api/moderator/moderator_api";
import SocketHandler from "../../../utils/socketHandler";

interface YouTubeRequest {
  id: string;
  username: string;
  youtube_url?: string;
  message?: string;
  song_name_with_artist?: string;
  timestamp: number;
}

interface YoutubeRequestProps {
  bookingId: string;
}

const extractVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const formatTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export default function YoutubeRequest({ bookingId }: YoutubeRequestProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [copySnackbar, setCopySnackbar] = useState(false);
  const socketSubscribedRef = useRef(false);

  const {
    data: youtubeRequests,
    isLoading: youtubeRequestsLoading,
    refetch: refetchYoutubeRequests,
  } = useGetYoutubeRequests({
    params: { id: bookingId ?? "" },
    key: ["youtube-requests", bookingId],
  });

  useEffect(() => {
    if (socketSubscribedRef.current || !bookingId) return;
    const socketHandler = SocketHandler.getInstance();
    socketHandler.subscribeToYoutubeUrlReceived(bookingId, () => {
      refetchYoutubeRequests();
    });
    socketSubscribedRef.current = true;
    return () => {
      socketHandler.unsubscribeToYoutubeUrlReceived(bookingId);
      socketSubscribedRef.current = false;
    };
  }, [bookingId, refetchYoutubeRequests]);

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySnackbar(true);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopySnackbar(true);
    }
  };

  const filteredRequests = (youtubeRequests?.data || []).filter(
    (request: YouTubeRequest) =>
      request.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.song_name_with_artist?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (youtubeRequestsLoading) {
    return (
      <div className="s4y-tool-empty">
        <div className="s4y-tool-empty-icon" style={{ fontSize: "1.4rem" }}>
          <CircularProgress size={22} sx={{ color: "var(--s4y-orange)" }} />
        </div>
        <div className="s4y-tool-empty-text">Loading requests…</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Search */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: "rgba(255,255,255,0.04)",
        border: "1px solid var(--s4y-border)",
        borderRadius: "10px",
        padding: "8px 12px",
      }}>
        <SearchIcon sx={{ fontSize: "1rem", color: "rgba(255,255,255,0.35)", flexShrink: 0 }} />
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by username or message…"
          style={{
            flex: 1,
            background: "none",
            border: "none",
            outline: "none",
            color: "#fff",
            fontSize: "0.8rem",
            fontFamily: "var(--s4y-font)",
          }}
        />
      </div>

      {/* List */}
      {filteredRequests.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid var(--s4y-border)",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <List dense disablePadding>
            {filteredRequests.map((request: YouTubeRequest, index: number) => (
              <React.Fragment key={request.id}>
                <ListItem
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderLeft: "3px solid var(--s4y-orange)",
                    "&:hover": { bgcolor: "rgba(249,115,22,0.05)" },
                    gap: 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
                        <YouTubeIcon sx={{ fontSize: "0.85rem", color: "var(--s4y-orange)" }} />
                        <span style={{
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          color: "#fff",
                          fontFamily: "var(--s4y-font)",
                          letterSpacing: "0.01em",
                        }}>
                          {request.song_name_with_artist?.trim()
                            ? request.song_name_with_artist
                            : (request.youtube_url ? (extractVideoId(request.youtube_url) || "Invalid URL") : "Song request")}
                        </span>
                      </div>
                    }
                    secondary={
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        {request.youtube_url && (
                          <span style={{
                            fontSize: "0.7rem",
                            color: "rgba(255,255,255,0.35)",
                            fontFamily: "var(--s4y-font)",
                            wordBreak: "break-all",
                          }}>
                            {request.youtube_url}
                          </span>
                        )}
                        {request.message && (
                          <span style={{
                            fontSize: "0.73rem",
                            color: "rgba(255,255,255,0.55)",
                            fontStyle: "italic",
                            fontFamily: "var(--s4y-font)",
                          }}>
                            "{request.message}"
                          </span>
                        )}
                        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                          <PersonIcon sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.35)" }} />
                          <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.4)", fontFamily: "var(--s4y-font)" }}>
                            {request.username}
                          </span>
                          <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.25)", fontFamily: "var(--s4y-font)" }}>
                            · {formatTime(request.timestamp)}
                          </span>
                        </div>
                      </div>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                      {request.song_name_with_artist?.trim() && (
                        <Tooltip title="Copy song request">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyLink(request.song_name_with_artist as string)}
                            sx={{
                              color: "rgba(255,255,255,0.35)",
                              "&:hover": { color: "var(--s4y-orange)", bgcolor: "rgba(249,115,22,0.1)" },
                              width: 28,
                              height: 28,
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: "0.8rem" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {request.youtube_url?.trim() && (
                        <Tooltip title="Copy YouTube link">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyLink(request.youtube_url as string)}
                            sx={{
                              color: "rgba(255,255,255,0.35)",
                              "&:hover": { color: "var(--s4y-orange)", bgcolor: "rgba(249,115,22,0.1)" },
                              width: 28,
                              height: 28,
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: "0.8rem" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < filteredRequests.length - 1 && (
                  <Divider sx={{ borderColor: "var(--s4y-border)" }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Empty state */}
      {filteredRequests.length === 0 && (
        <div className="s4y-tool-empty">
          <div className="s4y-tool-empty-icon">▶️</div>
          <div className="s4y-tool-empty-title">No requests yet</div>
          <div className="s4y-tool-empty-text">
            {searchTerm
              ? "No results match your search."
              : "Audience video requests will appear here during your live session."}
          </div>
        </div>
      )}

      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setCopySnackbar(false)}
          sx={{ bgcolor: "rgba(76,175,80,0.15)", color: "#b7f7c5", border: "1px solid rgba(34,197,94,0.3)" }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </div>
  );
}
